import * as React from 'react';
import styled, { css } from 'styled-components';

import { Icon } from '@creditornot/alchemy-react';
import * as icons from '@creditornot/alchemy-react/icons';
import { addInteractionEvent } from '@creditornot/browser-telemetry';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import { WoltWarehouseItem } from 'common/api-types/warehouse';
import { ContentContainer } from 'common/components/cookbook/ContentContainer';
import { DetailItem } from 'common/components/cookbook/DetailItem';
import { Typography as T } from 'common/components/cookbook/Typography/Typography';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { getResizedImageUrl } from 'common/utils/imageUtils';
import { InventoryQuantity } from 'features/warehouse/common/components/InventoryQuantity';

import { ReservedQuantity } from './ReservedQuantity';

const Root = styled(ContentContainer)`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid ${color.border};
`;

const TopRow = styled.div<{ $showLargeImage: boolean }>`
  display: flex;
  gap: 16px;
  ${({ $showLargeImage }) =>
    $showLargeImage &&
    css`
      flex-direction: column-reverse;
    `};
`;

const LeftColumn = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
`;

const ImageContainer = styled.div<{ $showLargeImage: boolean; $showBorder: boolean }>`
  width: 100%;

  ${({ $showBorder }) =>
    $showBorder &&
    css`
      border: 1px solid ${color.border};
    `}

  ${({ $showLargeImage }) =>
    $showLargeImage
      ? css`
          position: relative;
          padding-bottom: 56%;
          border-radius: 8px;
          overflow: hidden;
          z-index: 0;
          height: 0;
          margin-left: 0;
          margin-bottom: 16px;
        `
      : css`
          display: flex;
          align-items: center;
          justify-content: center;
          max-height: 188px;
          max-width: 334px;
          border-radius: 8px;
        `}
`;

const Image = styled.img<{ $showLargeImage: boolean }>`
  object-fit: cover;
  width: 100%;
  height: 100%;
  cursor: pointer;
  border-radius: 8px;
  border: 1px solid ${color.border};
  background: ${color.bgSurface};

  ${({ $showLargeImage }) =>
    $showLargeImage &&
    css`
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      width: 100%;
      height: 100%;
    `};
`;

const ImageNotAvailableText = styled.span`
  color: ${color.textSubdued};
`;

const BarcodeContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 4px;
`;

const IconContainer = styled.div`
  margin-right: 8px;
`;

const BottomRow = styled.div`
  display: flex;
  margin-top: 16px;
`;

const InventoryInformation = styled(T.body)`
  display: flex;
  gap: 8px;
`;

const InformationColumn = styled.div`
  flex: 1;
`;

type Props = {
  item: WoltWarehouseItem;
  view?: 'add_to_inventory' | 'remove_from_inventory' | 'adjust_inventory';
};

export const ItemInformation = ({ item, view }: Props) => {
  const formatMessage = useFormatMessage();
  const [showLargeImage, setShowLargeImage] = React.useState(false);

  const handleClickImage = () => {
    setShowLargeImage((showLargeImage) => !showLargeImage);

    // Send telemetry data when enlarging the image
    // and only for add_to_inventory view for now.
    if (view !== undefined && !showLargeImage) {
      addInteractionEvent(view, 'click', {
        click_target: 'product_image',
        item_id: item.id,
        gtin: item.gtin,
      });
    }
  };

  return (
    <Root>
      <TopRow $showLargeImage={showLargeImage}>
        <LeftColumn>
          <T.Heading level={2} variant="headline" data-test-id="item-name">
            {item.name}
          </T.Heading>
          <BarcodeContainer>
            <IconContainer>
              <Icon source={icons.barcode} size="small" fill={color.text} />
            </IconContainer>
            <T.body data-test-id="item-gtin">
              {item.gtin || formatMessage('warehouse.common.gtin_not_available')}
            </T.body>
          </BarcodeContainer>
        </LeftColumn>
        <ImageContainer $showLargeImage={showLargeImage} $showBorder={!item.image}>
          {item.image ? (
            <Image
              src={getResizedImageUrl(item.image, showLargeImage ? 1600 : 300)}
              onClick={handleClickImage}
              $showLargeImage={showLargeImage}
            />
          ) : (
            <ImageNotAvailableText>
              {formatMessage('common.not_available_acronym')}
            </ImageNotAvailableText>
          )}
        </ImageContainer>
      </TopRow>
      <BottomRow>
        <InformationColumn>
          <DetailItem label={formatMessage('warehouse.common.shelf_location')}>
            {(item.location?.name !== 'Unknown' && item.location?.name) ||
              formatMessage('warehouse.common.unknown_location')}
          </DetailItem>
        </InformationColumn>

        <InformationColumn>
          <DetailItem
            label={formatMessage('warehouse.common.inventory_quantity')}
            data-test-id="item-inventory-quantity"
          >
            <InventoryInformation data-test-id="item-inventory-quantity">
              <InventoryQuantity
                quantity={item.inventoryQuantity}
                measurementUnit="UNITS" // Only UNITS are supported in legacy tool
              />
              {!!item.reservedQuantity && (
                <ReservedQuantity
                  reservedQuantity={item.reservedQuantity}
                  measurementUnit="UNITS" // Only UNITS are supported in legacy tool
                />
              )}
            </InventoryInformation>
          </DetailItem>
        </InformationColumn>
      </BottomRow>
    </Root>
  );
};
